import React, { useEffect, useState } from "react";
import { CheckCircle2, FileText, FolderTree, ShieldAlert, Database, Briefcase, Wrench } from "lucide-react";

/**
 * RoutingAnimation — fullscreen overlay shown after every successful form
 * submission. Cinematically traces the document moving from the form
 * through every routing destination (PM Folder → Safety → Job Folder →
 * Backup) over ~2.4s, then fades and unmounts.
 *
 * Wired via the global event "forgedops-routing" (dispatched by
 * `lib/routingToast.showRoutingConfirmation`). Mount once at the App root.
 */
const ICONS = {
  "Form":           FileText,
  "PM":             Briefcase,
  "PM Folder":      Briefcase,
  "Safety":         ShieldAlert,
  "Safety Folder":  ShieldAlert,
  "Job Folder":     FolderTree,
  "Admin Records":  Database,
  "Admin":          Database,
  "Shop":           Wrench,
  "Shop Queue":     Wrench,
  "Backup":         Database,
  "Backup System":  Database,
};

function pickIcon(label) {
  const key = Object.keys(ICONS).find((k) => label.toLowerCase().includes(k.toLowerCase()));
  return key ? ICONS[key] : FolderTree;
}

export default function RoutingAnimation() {
  const [event, setEvent] = useState(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const handler = (e) => {
      const { kind, destinations } = e.detail || {};
      if (!destinations || destinations.length === 0) return;
      setEvent({ kind, dests: destinations.slice(0, 5) });
      setStep(0);
    };
    window.addEventListener("forgedops-routing", handler);
    return () => window.removeEventListener("forgedops-routing", handler);
  }, []);

  useEffect(() => {
    if (!event) return;
    if (step >= event.dests.length + 1) {
      const t = setTimeout(() => setEvent(null), 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep((s) => s + 1), 380);
    return () => clearTimeout(t);
  }, [event, step]);

  if (!event) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6 pointer-events-none animate-fadein"
      data-testid="forgedops-routing-overlay"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(3px)" }}
    >
      <div
        className="relative w-full max-w-2xl rounded-xl border-2 overflow-hidden pointer-events-auto"
        style={{ background: "#0B0B0B", borderColor: "rgba(255,106,0,0.45)" }}
      >
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, #FF6A00, transparent)" }}
        />
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center gap-3 border-b" style={{ borderColor: "#1A1A1A" }}>
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md"
            style={{ background: "#FF6A00", color: "#000" }}
          >
            <CheckCircle2 className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>
              Auto-routing complete
            </div>
            <div className="font-display text-lg font-black text-white truncate">
              {event.kind || "Submission"} routed
            </div>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#BDBDBD" }}>
            no manual email
          </span>
        </div>

        {/* Chain visualization */}
        <div className="px-6 py-7">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
            <ChainNode label="Form" Icon={FileText} active={step >= 1} done={step > 1} primary />
            {event.dests.map((d, i) => {
              const Icon = pickIcon(d);
              const active = step >= i + 2;
              const done = step > i + 2;
              return (
                <React.Fragment key={d}>
                  <ChainArrow active={active} />
                  <ChainNode label={d} Icon={Icon} active={active} done={done} />
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-5 flex flex-wrap gap-1.5">
            {event.dests.map((d, i) => (
              <span
                key={d}
                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded transition"
                style={{
                  background: step > i + 1 ? "rgba(46,125,50,0.18)" : "#141414",
                  color: step > i + 1 ? "#65DA76" : "#BDBDBD",
                  border: step > i + 1 ? "1px solid rgba(46,125,50,0.45)" : "1px solid #2A2A2A",
                }}
              >
                <CheckCircle2 className="inline w-3 h-3 mr-1" />
                {d}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChainNode({ label, Icon, active, done, primary }) {
  const color = done ? "#2E7D32" : active ? "#FF6A00" : "#2A2A2A";
  const text  = done ? "#65DA76" : active ? "#FF6A00" : "#8A8A8A";
  return (
    <div className="flex flex-col items-center gap-2 shrink-0 transition-transform duration-300" style={{ transform: active ? "scale(1)" : "scale(0.92)", opacity: active ? 1 : 0.55 }}>
      <span
        className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg transition-all"
        style={{
          background: active ? `${color}1F` : "#0F0F0F",
          border: `2px solid ${color}`,
          color: text,
          boxShadow: active && !done ? `0 0 24px ${color}66` : "none",
        }}
      >
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-center max-w-[80px] leading-tight" style={{ color: text }}>
        {label}
      </span>
    </div>
  );
}

function ChainArrow({ active }) {
  return (
    <span className="flex-1 min-w-[18px] sm:min-w-[28px] h-0.5 rounded transition-colors" style={{ background: active ? "#FF6A00" : "#1F1F1F" }} />
  );
}
